import React, { useCallback, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/core';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { getQuizQuestion, getQuizQuestionOptions } from 'redux/selectors/quizzes';
import { ResponseOption } from 'redux/schemas/models/quiz';
import { RootState } from 'redux/schemas';
import { ComponentType } from 'redux/schemas/models/lecture-component';

import NvPopover from 'shared/components/nv-popover';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import { editQuizQuestionOption } from 'redux/actions/quizzes';
import QuestionSetContext, { QuestionContext, SavingIndicator } from '../../../../lecture_pages/services/question-set-context';
import TabelCellPopoverContent from './table-answer-setting-popover';
import ReadOnlyCell from './table-read-only-cell';
import AnswerCell from './table-answer-cell';

type TableCellProps = {
  currentQuestionId: number;
  childrenQuestions?: number[];
  tableQuestionId?: number;
  rowNumber: number;
  columnNumber: number;
};

export enum CellMode {
  READONLY_MODE = 'readOnly',
  ANSWER_MODE = 'answer',
}

const styles = css`
  width: 100%;
  height: 100%;
  .popover-wrapper {
    height: 100%;
  }
`;

const popoverStyles = css`
  max-width: none !important;
`;

const TableCell = (props: TableCellProps) => {
  const {
    currentQuestionId,
    childrenQuestions,
    tableQuestionId,
    rowNumber,
    columnNumber,
  } = props;

  const dispatch = useDispatch();
  const currentQuestion = useSelector((state: RootState) => getQuizQuestion(state, currentQuestionId));
  const responseOptions: ResponseOption[] = useSelector(
    (state: RootState) => getQuizQuestionOptions(state, currentQuestion?.id),
  );
  const { isEditMode, isCurrentActivityType } = useQuestionSetHelpers();
  const { readOnly } = responseOptions[0];

  // This question context is for child question
  const questionContextValue = {
    responseOptions,
    currentQuestion,
  };

  const {
    scrollRef,
    savingIndicatorTimeoutRef,
    setSavingStatus,
  } = useContext(QuestionSetContext);
  const { registerSaving } = useContext(SavingRegistryContext);
  const modalContainer: HTMLDivElement = scrollRef?.current?.closest('.bs4-modal-content');

  const [cellMode, setCellMode] = useState(readOnly ? CellMode.READONLY_MODE : CellMode.ANSWER_MODE);
  const [showPopover, setShowPopover] = useState(false);

  const [shortAnswer, setShortAnswer] = useState(responseOptions[0].optionContent);
  const [isAnswerInRange, setIsAnswerInRange] = useState(false);

  useEffect(() => {
    if (readOnly) {
      setCellMode(CellMode.READONLY_MODE);
    } else {
      setCellMode(CellMode.ANSWER_MODE);
    }
  }, [readOnly]);

  const handleSwitch = useCallback((mode) => {
    setCellMode(mode);
    if (mode === CellMode.ANSWER_MODE) {
      setShowPopover(true);
    } else {
      setShowPopover(false);
    }
    const unregister = registerSaving();
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    dispatch(editQuizQuestionOption(
      {
        id: responseOptions[0].id,
        patch: {
          readOnly: (mode === CellMode.READONLY_MODE),
          explanation: null,
          secondaryExplanation: null,
        },
      },
    )).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);

      // setting back cellMode to initial state when API is rejected
      setCellMode((prevMode) => {
        if (prevMode === CellMode.ANSWER_MODE) {
          return CellMode.READONLY_MODE;
        }
        return CellMode.ANSWER_MODE;
      });
      setShowPopover(!showPopover);
    }).finally(() => {
      unregister();
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  }, [
    dispatch,
    registerSaving,
    responseOptions,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    showPopover,
  ]);

  const getCellElement = () => {
    switch (cellMode) {
      case CellMode.READONLY_MODE:
        return (
          <ReadOnlyCell
            handleSwitch={handleSwitch}
          />
        );
      case CellMode.ANSWER_MODE:
        return (
          <AnswerCell
            handleSwitch={handleSwitch}
            showPopover={showPopover}
            setShowPopover={setShowPopover}
            isAnswerInRange={isAnswerInRange}
            setIsAnswerInRange={setIsAnswerInRange}
            shortAnswer={shortAnswer}
            setShortAnswer={setShortAnswer}
            childrenQuestions={childrenQuestions}
            tableQuestionId={tableQuestionId}
            rowNumber={rowNumber}
            columnNumber={columnNumber}
          />
        );
      default:
        return null;
    }
  };

  return (
    <QuestionContext.Provider value={questionContextValue} key={currentQuestionId}>
      <div css={styles}>
        {isEditMode ? (
          <NvPopover
            show={showPopover && !isCurrentActivityType(ComponentType.SURVEY)}
            content={(
              <TabelCellPopoverContent
                shortAnswer={shortAnswer}
                setShortAnswer={setShortAnswer}
                isAnswerInRange={isAnswerInRange}
                setIsAnswerInRange={setIsAnswerInRange}
              />
            )}
            placement='top'
            rootClose
            overlayStyles={popoverStyles}
            onHide={() => setShowPopover(false)}
            container={modalContainer}
          >
            <div
              className={`${cellMode === CellMode.ANSWER_MODE ? 'bg-gray-7' : ''} h-100`}
            >
              {getCellElement()}
            </div>
          </NvPopover>
        ) : (
          getCellElement()
        )}
      </div>
    </QuestionContext.Provider>
  );
};

export default TableCell;
